import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPosts, type PostItem } from '@suangua/shared';
import { EditorModal } from '../components/board/EditorModal';
import { Avatar, BOARD_LABEL, fromNow, postTitle, verdictClass, verdictLabel } from '../components/ui/atoms';
import { useUI } from '../lib/ui/UIContext';

/**
 * 留言板列表页（原型 board 屏复刻）：
 * 页头（发帖金钮）+ 板块分段选择 + board-grid（帖子列表 + 桌面侧栏）。
 * 侧栏数据：一次拉取各板块首页（size=20）汇总 —— 社区统计 / 热门帖 / 板块计数。
 * 公告板块（announcement）仅管理端可发帖：该 tab 下隐藏「发帖」按钮。
 */

/** 板块（all=全部聚合视图；announcement 仅管理端可发帖） */
const BOARDS = ['all', 'chat', 'divination', 'qa', 'announcement'] as const;
type BoardKey = (typeof BOARDS)[number];
/** 可发帖板块（全部/公告不提供用户发帖入口） */
const POSTABLE: readonly string[] = ['chat', 'divination', 'qa'];
/** 板块可发帖判定（类型守卫：窄化为编辑器支持的板块） */
const isPostable = (b: BoardKey): b is 'chat' | 'divination' | 'qa' => POSTABLE.includes(b);

export function BoardPage() {
  const navigate = useNavigate();
  const { showToast } = useUI();
  const [board, setBoard] = useState<BoardKey>('all');
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  // 各板块数据缓存：items + total（首页 size=20）
  const emptyCache = () => ({
    all: { items: [] as PostItem[], total: 0 },
    chat: { items: [] as PostItem[], total: 0 },
    divination: { items: [] as PostItem[], total: 0 },
    qa: { items: [] as PostItem[], total: 0 },
    announcement: { items: [] as PostItem[], total: 0 },
  });
  const [cache, setCache] = useState<Record<BoardKey, { items: PostItem[]; total: number }>>(emptyCache);

  /** 拉取指定板块首页并写入缓存 */
  const loadBoard = useCallback(async (b: BoardKey) => {
    const res = await fetchPosts({ board: b, page: 1, size: 20 });
    setCache((c) => ({ ...c, [b]: { items: res.items, total: res.total } }));
  }, []);

  // 挂载：拉取三板块（侧栏社区数据 / 热门帖需要跨板块汇总）
  useEffect(() => {
    BOARDS.forEach((b) => void loadBoard(b).catch(() => undefined));
  }, [loadBoard]);

  // 板块切换 / 发布后：刷新当前板块
  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadBoard(board)
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [board, loadBoard]);

  const items = cache[board].items;
  const totals: Record<BoardKey, number> = {
    all: cache.all.total,
    chat: cache.chat.total,
    divination: cache.divination.total,
    qa: cache.qa.total,
    announcement: cache.announcement.total,
  };

  /** 发布成功 → 刷新当前板块 + 跳详情 */
  function onPosted(id: number) {
    setEditorOpen(false);
    showToast('发布成功');
    navigate(`/board/${id}`);
  }

  return (
    <section>
      {/* 页头（公告板块仅管理端可发，隐藏发帖入口） */}
      <div className="page-head">
        <h1 className="page-title font-zh">留言板</h1>
        <span className="page-sub">轻量社区 · Markdown</span>
        {/* 发帖入口（全部为聚合视图、公告仅管理端可发） */}
        {isPostable(board) && (
          <button className="gold-btn" onClick={() => setEditorOpen(true)}>
            <i className="fa-solid fa-plus" /> 发帖
          </button>
        )}
      </div>

      {/* 板块分段选择（公告在最右，窄屏自动换行到下一行） */}
      <div className="seg flex-wrap">
        {BOARDS.map((b) => (
          <button key={b} className={board === b ? 'on' : ''} onClick={() => setBoard(b)}>
            {BOARD_LABEL[b]}
          </button>
        ))}
      </div>

      <div className="board-grid mt-4">
        {/* 帖子列表 */}
        <div className="space-y-3">
          {loading && items.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-t3">加载中…</p>
          ) : items.length === 0 ? (
            <div className="empty-card">
              <i className="fa-regular fa-comment-dots" />
              <p>{board === 'announcement' ? '暂无公告' : '暂无帖子，来发第一帖吧'}</p>
            </div>
          ) : (
            items.map((post) => (
              <PostCard key={post.id} post={post} onClick={() => navigate(`/board/${post.id}`)} />
            ))
          )}
          {totals[board] > 20 && (
            <p className="text-center text-[11px] text-t3">共 {totals[board]} 帖 · 已展示前 20 条</p>
          )}
        </div>

        {/* 桌面侧栏（窄窗口隐藏；跨板块汇总数据） */}
        <SideBoard
          board={board}
          totals={totals}
          allItems={[...cache.chat.items, ...cache.divination.items, ...cache.qa.items, ...cache.announcement.items]}
          onSelectBoard={setBoard}
          onOpenPost={(id) => navigate(`/board/${id}`)}
        />
      </div>

      <EditorModal
        open={editorOpen}
        board={isPostable(board) ? board : 'chat'}
        onClose={() => setEditorOpen(false)}
        onPosted={onPosted}
      />
    </section>
  );
}

/** 桌面侧栏：社区数据 / 热门帖子 / 板块计数（数据来自三板块首页汇总） */
function SideBoard(props: {
  board: BoardKey;
  totals: Record<BoardKey, number>;
  allItems: PostItem[];
  onSelectBoard: (b: BoardKey) => void;
  onOpenPost: (id: number) => void;
}) {
  const { board, totals, allItems, onSelectBoard, onOpenPost } = props;

  // 社区数据：今日新帖（按已加载数据近似）/ 帖子总数 / 回复总数
  const todayStr = new Date().toDateString();
  const todayPosts = allItems.filter((p) => new Date(p.created_at).toDateString() === todayStr).length;
  const totalPosts = totals.chat + totals.divination + totals.qa;
  const totalReplies = allItems.reduce((n, p) => n + (p.reply_count || 0), 0);

  // 热门帖：按点赞取前 4
  const hotPosts = [...allItems].sort((a, b) => (b.like_count || 0) - (a.like_count || 0)).slice(0, 4);

  return (
    <aside className="board-side space-y-4">
      <div className="ink-card side-card">
        <h2 className="section-title">社 区 数 据</h2>
        <div className="grid grid-cols-3 divide-x divide-line">
          <div className="side-stat">
            <p>{todayPosts}</p>
            <p>今日新帖</p>
          </div>
          <div className="side-stat">
            <p>{totalPosts}</p>
            <p>帖子总数</p>
          </div>
          <div className="side-stat">
            <p>{totalReplies}</p>
            <p>回复总数</p>
          </div>
        </div>
      </div>

      <div className="ink-card side-card">
        <h2 className="section-title">热 门 帖 子</h2>
        {hotPosts.length === 0 ? (
          <p className="py-3 text-[12px] text-t3">暂无数据</p>
        ) : (
          hotPosts.map((p, i) => (
            <button key={p.id} className="side-row" onClick={() => onOpenPost(p.id)}>
              <span className="side-rank">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-t1">{postTitle(p)}</span>
              <span className="shrink-0 text-[10px] text-t3">
                <i className="fa-regular fa-thumbs-up" /> {p.like_count}
              </span>
            </button>
          ))
        )}
      </div>

      <div className="ink-card side-card">
        <h2 className="section-title">板 块</h2>
        {BOARDS.map((b) => (
          <button key={b} className="side-row" onClick={() => onSelectBoard(b)}>
            <span className={`min-w-0 flex-1 text-[12px] ${board === b ? 'text-gold-400' : 'text-t1'}`}>
              {BOARD_LABEL[b]}
            </span>
            <span className="text-[10px] text-t3">{totals[b]} 帖</span>
          </button>
        ))}
      </div>

      <p className="px-1 text-[11px] leading-relaxed text-t3">
        友善交流 · 理性讨论 · 禁止广告引流
        <br />
        占卜内容仅供娱乐参考
      </p>
    </aside>
  );
}

/** 帖子卡片（紧凑版：作者行 / 标题 / 数据行，共 3 行；不展示正文摘要） */
function PostCard({ post, onClick }: { post: PostItem; onClick: () => void }) {
  const showVerdict = post.board === 'divination' && post.verdict;
  return (
    <article className="ink-card post-card px-4 py-3" onClick={onClick}>
      <div className="flex items-center gap-2.5">
        <Avatar name={post.username} url={post.avatar_url} size="sm" />
        <p className="min-w-0 flex-1 truncate text-[12px] leading-tight text-t1">{post.username}</p>
        <span className="shrink-0 text-[11px] text-t3">{fromNow(post.created_at)}</span>
        {post.is_pinned === 1 && <span className="chip-xs text-cinnabar-500">置顶</span>}
        {post.is_locked === 1 && <span className="chip-xs text-t3">只读</span>}
        {showVerdict && (
          <span className={`verdict-seal ${verdictClass(post.verdict as string)}`}>
            {verdictLabel(post.verdict as string)}
          </span>
        )}
      </div>
      <h3 className="mt-1.5 truncate text-[14px] font-semibold leading-snug text-t1">{postTitle(post)}</h3>
      <div className="mt-1.5 flex items-center gap-4 text-[11px] leading-none text-t2">
        <span>
          <i className="fa-regular fa-comment" /> {post.reply_count}
        </span>
        <span>
          <i className="fa-regular fa-thumbs-up" /> {post.like_count}
        </span>
        <span className="ml-auto rounded-full bg-fill px-2 py-0.5">{BOARD_LABEL[post.board]}</span>
      </div>
    </article>
  );
}
